import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Paperclip, Undo2 } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { Carregando, Erro } from '../../componentes/estados';
import { Rotulo, AreaSensivel } from '../../componentes/formulario';
import { Drawer, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { data, dinheiro, nomePessoa } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { corDoValor, invalidarFinanceiro, type Lancamento } from './tipos';
import { descricaoGerada } from '../../nucleo/texto-sistema';
import { TextoCortado } from '../../componentes/texto-cortado';
import { anunciarOcultos } from '../../componentes/sensivel';
import { rotuloCategoria } from '../../nucleo/rotulos';

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 justify-between gap-4 py-1.5 text-sm">
      <dt className="shrink-0 text-tinta-3">{rotulo}</dt>
      <TextoCortado como="dd" className="min-w-0 text-right">{children}</TextoCortado>
    </div>
  );
}

/** Ficha do lançamento: dados, comprovante, estorno (nunca apaga) e atividade. */
export function DetalheLancamento({ id, aoFechar }: { id: string | null; aoFechar: () => void }) {
  const { t } = useTranslation('financeiro');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['/financeiro/lancamentos', 'obter', id], queryFn: () => get<Lancamento>(`/financeiro/lancamentos/${id}`), enabled: !!id });
  const [estornando, setEstornando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [chave, setChave] = useState(() => crypto.randomUUID());

  const estornar = useMutation({
    mutationFn: () => post<{ estorno_id: string }>(`/financeiro/lancamentos/${id}/estornar`, { motivo: motivo.trim() || undefined }, { 'Idempotency-Key': chave }),
    onSuccess: async () => {
      await invalidarFinanceiro(qc);
      setEstornando(false);
      setMotivo('');
      setChave(crypto.randomUUID());
      toast.sucesso(t('detalhe.estornado'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  const l = q.data;
  const podeEstornar = !!l && s.pode('financeiro.estornar') && !l.estorno_de && !l.estornado_por;
  const sinal = l?.tipo === 'entrada' ? '+' : l?.tipo === 'saida' ? '-' : '';
  return (
    <>
      <Drawer
        aberto={!!id}
        aoFechar={aoFechar}
        largura="lg"
        titulo={l ? descricaoGerada(l.descricao) : t('detalhe.titulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.fechar')}
            </Botao>
            {podeEstornar && (
              <Botao variante="perigo" icone={<Undo2 className="size-4" />} onClick={() => setEstornando(true)}>
                {t('acoes.estornar')}
              </Botao>
            )}
          </>
        }
      >
        {q.isPending && <Carregando />}
        {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
        {l && (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Selo cor={l.tipo === 'entrada' ? 'verde' : l.tipo === 'saida' ? 'perigo' : 'azul'}>{t(`tipo.${l.tipo}`)}</Selo>
              {l.conciliado && <Selo cor="verde">{t('estado.conciliado')}</Selo>}
              {l.estorno_de && <Selo cor="laranja">{t('estado.estorno')}</Selo>}
              {l.estornado_por && <Selo cor="laranja">{t('estado.estornado')}</Selo>}
            </div>
            <p className={`numero mb-4 text-3xl font-semibold ${corDoValor(l.tipo)}`}>
              {sinal}
              {anunciarOcultos(dinheiro(l.valor_centavos, l.moeda))}
            </p>
            <dl className="divide-y divide-borda">
              <Linha rotulo={t('campos.conta')}>{l.tipo === 'transferencia' ? `${l.conta_nome} > ${l.conta_destino_nome}` : l.conta_nome}</Linha>
              <Linha rotulo={t('campos.dataCaixa')}>{data(l.data_caixa)}</Linha>
              <Linha rotulo={t('campos.dataCompetencia')}>{data(l.data_competencia)}</Linha>
              {l.categoria_nome && <Linha rotulo={t('campos.categoria')}>{rotuloCategoria(l.categoria_nome)}</Linha>}
              {l.centro_custo_nome && <Linha rotulo={t('campos.centroCusto')}>{l.centro_custo_nome}</Linha>}
              {l.parceiro_nome && <Linha rotulo={t('campos.parceiro')}>{nomePessoa(l.parceiro_nome)}</Linha>}
              {l.origem && (
                <Linha rotulo={t('campos.origem')}>
                  {l.origem.modulo} / {l.origem.entidade}
                </Linha>
              )}
            </dl>
            {l.comprovante_anexo_id && (
              <div className="mt-4">
                <Rotulo id="comprovante" rotulo={t('campos.comprovante')}>
                  <a id="comprovante" href={`/api/v1/anexos/${l.comprovante_anexo_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-acento-forte hover:underline">
                    <Paperclip className="size-4" aria-hidden />
                    {t('detalhe.abrirComprovante')}
                  </a>
                </Rotulo>
              </div>
            )}
            {(l.estorno_de || l.estornado_por) && <p className="mt-4 text-sm text-tinta-3">{l.estorno_de ? t('detalhe.ehEstorno') : t('detalhe.foiEstornado')}</p>}
            <div className="mt-6 border-t border-borda pt-4">
              <Chatter entidade="financeiro.lancamento" moeda={l.moeda} registroId={l.id} titulo={l.descricao} rotulosCampos={{ conciliado: t('estado.conciliado'), estornado_por: t('estado.estornado') }} />
            </div>
          </>
        )}
      </Drawer>
      <Modal
        aberto={estornando}
        aoFechar={() => setEstornando(false)}
        titulo={t('estorno.titulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setEstornando(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="perigo" carregando={estornar.isPending} onClick={() => estornar.mutate()}>
              {t('acoes.estornar')}
            </Botao>
          </>
        }
      >
        <p className="mb-3 text-sm text-tinta-2">{t('estorno.texto')}</p>
        <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
          {t('estorno.motivo')}
          <AreaSensivel rows={3} maxLength={300} value={motivo} onChange={(e) => setMotivo(e.target.value)} className="w-full rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco" />
        </label>
      </Modal>
    </>
  );
}
